/**
 * CostToBuildAustraliaPage - national cost hub (/cost-to-build-a-house-australia).
 * Anchors on official ABS data, compares states, routes to SA + VIC detail hubs.
 * All figures sourced from data/buildCostData.js (single source of truth).
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import BuildChoicesStrip from '../../components/BuildChoicesStrip';
import QuickAnswer from '../../components/content/QuickAnswer';
import AIOverviewSchema from '../../components/seo/AIOverviewSchema';
import { CostByTierTable } from '../../components/content/CostByTierTable';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import {
  NATIONAL, STATE_ABS, BUILD_TYPES, COST_SOURCES, LAST_VERIFIED, TOTAL_BUDGET_NOTE, MARKET_PULSE,
} from '../../data/buildCostData';

const CostToBuildAustraliaPage = () => (
  <div className="min-h-screen bg-white">
    <Helmet>
      <title>Cost to Build a House in Australia (2026): Real $/m² by State | BuildPilot</title>
      <meta name="description" content="What it costs to build a house in Australia in 2026, official ABS averages plus current market tender rates by state and build type. Independent, sourced data from BuildPilot." />
      <link rel="canonical" href="https://buildpilot.com.au/cost-to-build-a-house-australia" />
    </Helmet>
    <Header />

    <main className="max-w-[880px] mx-auto px-5 sm:px-6 py-10 sm:py-14">
      <h1 className="text-4xl sm:text-5xl font-bold text-[#1d1d1f] tracking-tight mb-4">Cost to build a house in Australia (2026)</h1>

      <QuickAnswer question="How much does it cost to build a house in Australia in 2026?">
        <p data-speakable="true">
          The official ABS average for a newly completed detached house is <strong>{NATIONAL.absAvgCost}</strong> at <strong>{NATIONAL.absPerSqm}/m²</strong> (average floor area {NATIONAL.absFloorArea}, excluding land). That reflects contracts signed 12–24 months ago. For new 2026 contracts, market tender rates run higher, roughly <strong>$1,600–$3,200/m²</strong> for volume builders and <strong>$2,500–$7,600/m²</strong> for custom builds. Add about 25–45% for land, site works, fees and GST to reach a total project budget.
        </p>
      </QuickAnswer>
      <AIOverviewSchema
        question="How much does it cost to build a house in Australia in 2026?"
        answer={`The official ABS average for a newly completed detached house is ${NATIONAL.absAvgCost} at ${NATIONAL.absPerSqm}/m² (avg floor area ${NATIONAL.absFloorArea}, excluding land), based on 2024-25 completion data. For new 2026 contracts, market tender rates are higher: volume builders $1,600-$3,200/m², custom builds $2,500-$7,600/m², architectural $8,000+/m². State ABS averages per m²: NSW $2,396, QLD $1,987, VIC $1,914, SA $1,788, WA $1,585. Add 25-45% for land, site works, fees and GST for a total project budget.`}
        url="/cost-to-build-a-house-australia"
        mentions={['Cost to build a house', 'ABS Building Activity', 'Volume builder', 'Custom builder', 'New home construction']}
      />

      <p className="mt-5 text-[14px] text-[#424245] leading-[1.6] border-l-2 border-[#e8e8ed] pl-4" data-testid="market-pulse">
        <strong className="text-[#1d8efe]">Latest read ({MARKET_PULSE.asAt}):</strong> in the {MARKET_PULSE.activityPeriod}, the value of new residential building work done reached <strong>{MARKET_PULSE.valueNewResidential}</strong> ({MARKET_PULSE.valueNewResidentialChange}), with new houses at <strong>{MARKET_PULSE.valueNewHouses}</strong> ({MARKET_PULSE.valueNewHousesChange}). The average approval value for a new private house has reached <strong>{MARKET_PULSE.approvalsValue}</strong>, new-dwelling prices are up <strong>{MARKET_PULSE.dwellingInflation}</strong> over the year, and construction costs rose <strong>{MARKET_PULSE.constructionCost}</strong> in the June quarter, so forward budgets should sit above the older completion average. {MARKET_PULSE.source}.
      </p>

      {/* State comparison (ABS official) */}
      <section className="mt-10" data-testid="national-state-comparison">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1d1d1f] mb-2 tracking-tight">Average build cost by state (official ABS)</h2>
        <p className="text-[14px] text-[#424245] mb-4 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-[#1d8efe]" /> ABS Building Activity 2024–25, completed detached houses, excludes land.
        </p>
        <div className="overflow-x-auto border border-[#e8e8ed] rounded-xl">
          <table className="w-full text-sm min-w-[420px]" data-speakable="true">
            <thead>
              <tr className="bg-[#f5f5f7] text-left">
                <th className="px-4 py-3 font-semibold text-[#1d1d1f]">State</th>
                <th className="px-4 py-3 font-semibold text-[#1d1d1f]">Cost per m²</th>
                <th className="px-4 py-3 font-semibold text-[#1d1d1f]">Avg completed home</th>
              </tr>
            </thead>
            <tbody>
              {STATE_ABS.map((s, i) => (
                <tr key={s.state} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} data-testid={`national-row-${s.state.toLowerCase()}`}>
                  <td className="px-4 py-3 font-medium text-[#1d1d1f]">{s.state}</td>
                  <td className="px-4 py-3 text-[#424245]">{s.perSqm}</td>
                  <td className="px-4 py-3 text-[#424245]">{s.avgCost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[12px] text-[#86868b] mt-2">National average: {NATIONAL.absAvgCost} ({NATIONAL.absPerSqm}/m²). {NATIONAL.note}</p>
      </section>

      {/* Build type bands */}
      <div className="mt-10">
        <CostByTierTable
          heading="Cost by builder type (2026 market rates)"
          quickAnswer="Volume (project) builders run $1,600–$3,200/m², custom builders $2,500–$7,600/m², and architectural homes $8,000+/m², base build, excluding land."
          basisLabel=""
          tiers={BUILD_TYPES.map((b) => ({ tier: b.type, sub: b.note, perSqm: b.perSqm, total: '—' }))}
          notIncludedTo="/house-and-land-packages-adelaide"
          footnote={TOTAL_BUDGET_NOTE}
          sources={COST_SOURCES.slice(0, 3).map((s) => ({ label: s.label, url: s.url }))}
          testId="national-cost-by-type"
        />
      </div>

      {/* State hubs */}
      <section className="mt-10" data-testid="national-state-hubs">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1d1d1f] mb-4 tracking-tight">Detailed cost guides by city</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/building-costs-adelaide-2026" className="group rounded-2xl border border-[#e8e8ed] p-5 hover:border-[#1d8efe] transition-colors" data-testid="hub-link-adelaide">
            <h3 className="font-semibold text-[#1d1d1f] mb-1 flex items-center justify-between">Adelaide / SA <ArrowRight className="w-4 h-4 text-[#1d8efe] group-hover:translate-x-1 transition-transform" /></h3>
            <p className="text-[13px] text-[#6e6e73]">Full cost-by-tier and by-size tables for South Australia.</p>
          </Link>
          <Link to="/melbourne-building-costs" className="group rounded-2xl border border-[#e8e8ed] p-5 hover:border-[#1d8efe] transition-colors" data-testid="hub-link-melbourne">
            <h3 className="font-semibold text-[#1d1d1f] mb-1 flex items-center justify-between">Melbourne / VIC <ArrowRight className="w-4 h-4 text-[#1d8efe] group-hover:translate-x-1 transition-transform" /></h3>
            <p className="text-[13px] text-[#6e6e73]">Full cost-by-tier and by-size tables for Victoria.</p>
          </Link>
        </div>
        <p className="text-[13px] text-[#86868b] mt-4">More states coming soon.</p>
      </section>

      {/* Sources */}
      <section className="mt-10 border-t border-[#e8e8ed] pt-6" data-testid="national-sources">
        <h2 className="text-[15px] font-semibold text-[#1d1d1f] mb-2">Sources &amp; methodology</h2>
        <ul className="space-y-1.5">
          {COST_SOURCES.map((s) => (
            <li key={s.label} className="text-[13px] text-[#52525b]">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#1d8efe] hover:underline">{s.label}</a>
              {s.note ? `, ${s.note}` : ''}
            </li>
          ))}
        </ul>
        <p className="text-[12px] text-[#86868b] mt-3">All figures last verified {LAST_VERIFIED}. Cost ranges are indicative planning guides, always rely on a builder&apos;s fixed-price contract.</p>
      </section>
    </main>

    <BuildChoicesStrip
      slugs={['single-vs-double-storey', 'concrete-slab-vs-timber-stumps', 'what-is-hebel', 'brick-veneer-vs-double-brick', 'double-glazing-worth-it', 'provisional-sums-vs-pc-items']}
      title="The choices behind the cost"
      subtitle="The build decisions that move the $/m² most, explained in plain English for SA and Victoria."
    />

    <Footer />
  </div>
);

export default CostToBuildAustraliaPage;
